import { useEffect, useState } from "react";
import { useAdminStore } from "@/lib/stores/admin-store";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AlertCircle, Search, Trash2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Admin Experiences Management Page
 * With approval, rejection, and deletion functionality
 */
export function ExperiencesPage() {
  const { 
    isInitialized, 
    error, 
    experiences,
    pendingExperiences,
    fetchExperiences,
    fetchPendingExperiences,
    approveExperience,
    rejectExperience,
    deleteExperience
  } = useAdminStore();
  
  const [search, setSearch] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  // Refresh data when component mounts
  useEffect(() => {
    if (isInitialized) {
      Promise.all([
        fetchExperiences().catch(err => console.error("[AdminExperiences] Error refreshing experiences:", err)),
        fetchPendingExperiences().catch(err => console.error("[AdminExperiences] Error refreshing pending experiences:", err))
      ]);
    }
  }, [isInitialized, fetchExperiences, fetchPendingExperiences]);

  // Combine all experiences and filter
  const allExperiences = [...experiences, ...pendingExperiences.filter(
    // Avoid duplicates 
    pe => !experiences.some(e => e.id === pe.id)
  )];

  // Filter and search experiences
  const filteredExperiences = allExperiences.filter(exp => {
    // First filter by status
    if (statusFilter !== 'all' && exp.status !== statusFilter) {
      return false;
    }
    
    // Then filter by search term
    if (!search) return true;
    
    const searchTerm = search.toLowerCase();
    return (
      (exp.title && exp.title.toLowerCase().includes(searchTerm)) ||
      (exp.description && exp.description.toLowerCase().includes(searchTerm)) ||
      (exp.creator_name && exp.creator_name.toLowerCase().includes(searchTerm))
    );
  });

  // Handle experience deletion with confirmation
  const handleDeleteExperience = async (experienceId: string) => {
    try {
      setIsProcessing(true);
      await deleteExperience(experienceId);
      setShowConfirmDelete(null);
    } catch (error) {
      console.error("Failed to delete experience:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle approval
  const handleApproveExperience = async (experienceId: string) => {
    try {
      setIsProcessing(true);
      await approveExperience(experienceId);
    } catch (error) {
      console.error("Failed to approve experience:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle rejection
  const handleRejectExperience = async (experienceId: string) => {
    try {
      setIsProcessing(true);
      await rejectExperience(experienceId);
    } catch (error) {
      console.error("Failed to reject experience:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Full page loading state
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Errors banner */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-400 mr-3" />
            <div>
              <p className="text-sm text-red-300 font-medium">
                Error loading experiences
              </p>
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative w-full sm:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-sand-400" />
          </div>
          <input
            type="text"
            placeholder="Search experiences..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-earth-700 border border-earth-600 text-sand-200 placeholder-sand-500 block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-sand-400"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant={statusFilter === 'all' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
            className={statusFilter === 'all' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            All
          </Button>
          <Button
            variant={statusFilter === 'pending' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('pending')}
            className={statusFilter === 'pending' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            Pending
          </Button>
          <Button
            variant={statusFilter === 'approved' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('approved')}
            className={statusFilter === 'approved' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            Approved
          </Button>
          <Button
            variant={statusFilter === 'rejected' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('rejected')}
            className={statusFilter === 'rejected' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            Rejected
          </Button>
        </div>
      </div>

      {/* Experiences Table */}
      <div className="overflow-hidden shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-earth-600">
            <thead className="bg-earth-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                  Experience
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                  Creator
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-sand-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-earth-700 divide-y divide-earth-600">
              {filteredExperiences.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sand-400">
                    No experiences found
                  </td>
                </tr>
              ) : (
                filteredExperiences.map((experience) => (
                  <tr key={experience.id} className="hover:bg-earth-600/50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-sand-200">{experience.title || experience.name}</div>
                      <div className="text-xs text-sand-400 truncate max-w-xs">{experience.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-sand-300">{experience.creator_name || 'Unknown Creator'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        experience.status === 'approved' 
                          ? 'bg-green-900/30 text-green-300' 
                          : experience.status === 'pending'
                          ? 'bg-yellow-900/30 text-yellow-300'
                          : 'bg-red-900/30 text-red-300'
                      }`}>
                        {experience.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-sand-300">
                      ${experience.price || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end space-x-2">
                        {showConfirmDelete === experience.id ? (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={isProcessing}
                              onClick={() => handleDeleteExperience(experience.id)}
                              className="bg-red-500/20 text-red-300 hover:bg-red-500/30"
                            >
                              Confirm
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isProcessing}
                              onClick={() => setShowConfirmDelete(null)}
                              className="bg-earth-700 text-sand-300"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            {experience.status === 'pending' && (
                              <>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleApproveExperience(experience.id)}
                                  className="bg-green-500/20 text-green-300 hover:bg-green-500/30"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleRejectExperience(experience.id)}
                                  className="bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setShowConfirmDelete(experience.id)}
                              className="bg-red-500/20 text-red-300 hover:bg-red-500/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 